import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import {
    CheckCircle, XCircle, Briefcase, MapPin, Phone, Envelope, GraduationCap,
    IdentificationBadge, Calendar, ChartBar,
    Handshake, NotePencil, Sparkle, LinkSimple, Buildings,
} from "@phosphor-icons/react";

/* ===========================================================================
   Admin / Provider Applications — Path B Phase 4 (2026-02-25)
   ---------------------------------------------------------------------------
   Was a flat "show name/clinic/phone/city → Approve/Reject" view. Now exposes
   ALL the structured fields the new ApplyWizard collects, so reviewers can
   actually do their job (verify council registration, sanity-check years,
   compare specialty + cases-per-month claim against the practice intel).

   Also adds an `internal_review_notes` textarea per application — for
   spot-check findings ("verified council reg via KDC website 2026-02-25",
   "called LinkedIn, confirmed degree") — persisted via the new
   `PUT /api/admin/applications/{id}/notes` endpoint.

   Self-serve direct-apply and sales-led magic-link applications are both
   listed here, distinguished by the `source` field (shown as a pill in the
   header).
   =========================================================================== */

const STATUS_STYLES = {
    pending:  "bg-yellow-100 text-yellow-800 border-yellow-200",
    approved: "bg-emerald-100 text-emerald-700 border-emerald-200",
    rejected: "bg-rose-100 text-rose-700 border-rose-200",
};

const SOURCE_STYLES = {
    "doctor-application":          { label: "Direct apply",  cls: "bg-secondary text-foreground border-border" },
    "sales-invited-application":   { label: "Sales-invited", cls: "bg-cyan-100 text-cyan-800 border-cyan-200" },
};

const SPECIALTY_LABEL = {
    Orthodontist: "Orthodontist",
    GP_Dentist: "GP / General Dentist",
    Pedodontist: "Pedodontist",
    Prosthodontist: "Prosthodontist",
    Other: "Other",
};


export default function AdminApplications() {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState("pending");

    const load = () => {
        setLoading(true);
        api.get("/admin/applications").then((r) => setItems(r.data)).finally(() => setLoading(false));
    };

    useEffect(() => { load(); }, []);

    const act = async (id, action) => {
        try {
            await api.put(`/admin/applications/${id}/${action}`);
            toast.success(action === "approve" ? "Approved — doctor account is live" : "Application rejected");
            load();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Failed");
        }
    };

    const visible = items.filter((a) => filter === "all" ? true : (a.status || "pending") === filter);

    return (
        <div data-testid="admin-applications-page">
            <h1 className="font-display text-3xl font-bold mb-1">Provider Applications</h1>
            <p className="text-muted-foreground mb-6 text-sm">
                Review credentials carefully. Approving auto-creates the doctor profile + login.
            </p>

            <div className="flex gap-2 mb-6 flex-wrap" data-testid="apps-filter-row">
                {["pending", "approved", "rejected", "all"].map((f) => (
                    <button
                        key={f}
                        onClick={() => setFilter(f)}
                        className={`px-4 py-2 rounded-full text-sm capitalize transition-colors ${
                            filter === f ? "bg-primary text-white" : "bg-card border border-border hover:border-primary"
                        }`}
                        data-testid={`apps-filter-${f}`}
                    >
                        {f}{f !== "all" && ` (${items.filter((a) => (a.status || "pending") === f).length})`}
                    </button>
                ))}
            </div>

            {loading && <div className="text-muted-foreground">Loading…</div>}

            {!loading && visible.length === 0 && (
                <div className="bg-card border border-border rounded-2xl p-10 text-center text-muted-foreground">
                    <Briefcase size={32} className="mx-auto mb-2 opacity-50" />
                    No applications in this view.
                </div>
            )}

            <div className="space-y-5">
                {visible.map((a) => (
                    <ApplicationCard key={a.id} application={a} onAct={act} onSaved={load} />
                ))}
            </div>
        </div>
    );
}


function ApplicationCard({ application: a, onAct, onSaved }) {
    const status = a.status || "pending";
    const source = SOURCE_STYLES[a.source] || SOURCE_STYLES["doctor-application"];
    const [notes, setNotes] = useState(a.internal_review_notes || "");
    const [savingNotes, setSavingNotes] = useState(false);
    const lastNoteDate = a.internal_review_notes_at
        ? new Date(a.internal_review_notes_at).toLocaleString("en-IN", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })
        : null;

    const saveNotes = async () => {
        setSavingNotes(true);
        try {
            await api.put(`/admin/applications/${a.id}/notes`, { internal_review_notes: notes });
            toast.success("Internal review notes saved");
            onSaved?.();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Failed to save notes");
        }
        setSavingNotes(false);
    };

    return (
        <div className="bg-card border border-border rounded-2xl p-6 bento-shadow" data-testid={`application-${a.id}`}>
            {/* Header — identity + source pill + status pill */}
            <div className="flex items-start justify-between gap-3 mb-5 flex-wrap">
                <div>
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <span className="font-display text-xl font-bold">{a.name}</span>
                        <span className={`text-[10px] uppercase tracking-wide px-2 py-0.5 rounded-full border font-bold ${source.cls}`}>
                            {a.source === "sales-invited-application" && <Handshake size={9} weight="fill" className="inline mr-1" />}
                            {source.label}
                        </span>
                    </div>
                    <div className="text-sm text-primary font-medium flex items-center gap-1.5">
                        <Buildings size={14} weight="duotone" /> {a.clinic}
                        {a.clinic_registered_name && a.clinic_registered_name !== a.clinic && (
                            <span className="text-muted-foreground text-xs"> (Reg.: {a.clinic_registered_name})</span>
                        )}
                    </div>
                </div>
                <span className={`text-xs px-3 py-1.5 rounded-full font-bold capitalize border ${STATUS_STYLES[status]}`} data-testid={`app-status-${a.id}`}>
                    {status}
                </span>
            </div>

            {/* Contact + practice intel grid */}
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-3 text-sm mb-5">
                <Row icon={Phone}    label="Phone"       value={a.phone} mono />
                <Row icon={Envelope} label="Email"       value={a.email} />
                <Row icon={MapPin}   label="City"        value={a.city} />
                {a.address && <Row icon={MapPin} label="Address" value={a.address} className="sm:col-span-2" />}
                {a.gst_number && <Row label="GSTIN" value={a.gst_number} mono />}
            </div>

            {/* Credentials block — the meat of the review */}
            <div className="rounded-2xl bg-secondary/30 border border-border/60 p-5 mb-5" data-testid={`app-credentials-${a.id}`}>
                <SectionHeader icon={GraduationCap} title="Credentials" />
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-3 text-sm">
                    <Row label="Degree"               value={a.degree} />
                    <Row label="Specialty"            value={SPECIALTY_LABEL[a.specialty] || a.specialty} />
                    <Row label="Years in practice"    value={a.years_in_practice != null ? `${a.years_in_practice}` : "—"} />
                    <Row icon={IdentificationBadge}   label="Council reg. #"  value={a.council_registration_number} mono />
                    <Row label="State council"        value={a.state_council} />
                    {a.linkedin_url && (
                        <Row icon={LinkSimple} label="LinkedIn"
                             value={<a href={a.linkedin_url} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline break-all">{a.linkedin_url}</a>} />
                    )}
                </div>
                {a.board_certifications && (
                    <div className="mt-4 pt-3 border-t border-border/50">
                        <div className="text-[10px] uppercase tracking-wide font-bold text-muted-foreground mb-1">Board certifications</div>
                        <p className="text-sm whitespace-pre-wrap">{a.board_certifications}</p>
                    </div>
                )}
            </div>

            {/* Practice + goals block */}
            <div className="grid sm:grid-cols-2 gap-5 mb-5">
                <div className="rounded-2xl bg-card border border-border p-5">
                    <SectionHeader icon={ChartBar} title="Practice scale" />
                    <div className="space-y-2.5 text-sm">
                        <Row label="Cases / month"       value={a.cases_per_month} />
                        <Row label="Current brand"       value={a.current_aligner_brand} />
                        {a.clinic_photo_url && (
                            <Row icon={LinkSimple} label="Clinic photo URL"
                                 value={<a href={a.clinic_photo_url} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline break-all">{a.clinic_photo_url}</a>} />
                        )}
                    </div>
                </div>
                <div className="rounded-2xl bg-card border border-border p-5">
                    <SectionHeader icon={Sparkle} title="Goals" />
                    <div className="space-y-2.5 text-sm">
                        <Row label="Patient demographic" value={a.primary_patient_demographic} />
                        <Row label="Preferred fee"       value={a.preferred_consultation_fee != null ? `₹${a.preferred_consultation_fee}` : "—"} />
                    </div>
                </div>
            </div>

            {/* Why Invisique™ / message (free-text) */}
            {(a.why_invisique || a.message) && (
                <div className="rounded-2xl bg-card border border-border p-5 mb-5">
                    <SectionHeader icon={NotePencil} title="Why Invisique™" />
                    <p className="text-sm whitespace-pre-wrap">{a.why_invisique || a.message}</p>
                </div>
            )}

            {/* Internal review notes (admin only — never shown to applicant) */}
            <div className="rounded-2xl bg-amber-50 border border-amber-200 p-5 mb-5" data-testid={`app-notes-block-${a.id}`}>
                <SectionHeader icon={NotePencil} title="Internal review notes (admin only)" />
                <Textarea
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    rows={3}
                    placeholder="e.g. 'Verified council reg via KDC website 2026-02-25. Spoke with referrer Dr Patil — confirmed solo practice, no shared chair.'"
                    className="bg-white mb-3"
                    data-testid={`app-notes-input-${a.id}`}
                />
                <div className="flex items-center justify-between gap-3 flex-wrap">
                    <div className="text-[11px] text-muted-foreground">
                        {lastNoteDate
                            ? `Last edited by ${a.internal_review_notes_by || "?"} · ${lastNoteDate}`
                            : "Not yet recorded."}
                    </div>
                    <Button
                        size="sm" variant="outline"
                        onClick={saveNotes}
                        disabled={savingNotes || notes === (a.internal_review_notes || "")}
                        className="rounded-full"
                        data-testid={`app-notes-save-${a.id}`}
                    >
                        {savingNotes ? "Saving…" : "Save notes"}
                    </Button>
                </div>
            </div>

            {/* Footer — applied date + decision buttons */}
            <div className="flex items-center justify-between gap-2 flex-wrap pt-3 border-t border-border">
                <div className="text-xs text-muted-foreground flex items-center gap-3 flex-wrap">
                    <span className="inline-flex items-center gap-1"><Calendar size={12} /> Applied {new Date(a.created_at).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}</span>
                    {a.tos_agreed_at && <span className="inline-flex items-center gap-1 text-emerald-700"><CheckCircle size={12} weight="fill" /> ToS accepted</span>}
                    {a.b2b_lead_id && (
                        <a href={`/admin/b2b-leads/${a.b2b_lead_id}`} className="text-primary hover:underline inline-flex items-center gap-1">
                            <Handshake size={12} /> View originating lead
                        </a>
                    )}
                </div>
                {status === "pending" && (
                    <div className="flex gap-2">
                        <Button size="sm" variant="outline" onClick={() => onAct(a.id, "reject")} data-testid={`reject-${a.id}`}>
                            <XCircle size={14} className="mr-1" /> Reject
                        </Button>
                        <Button size="sm" onClick={() => onAct(a.id, "approve")} className="rounded-full" data-testid={`approve-${a.id}`}>
                            <CheckCircle size={14} className="mr-1" /> Approve & onboard
                        </Button>
                    </div>
                )}
            </div>
        </div>
    );
}


/* ---------------------------------------------------------------------------
   Atoms
   --------------------------------------------------------------------------- */

function SectionHeader({ icon: Icon, title }) {
    return (
        <div className="flex items-center gap-2 mb-3">
            <Icon size={16} weight="duotone" className="text-primary" />
            <span className="text-xs uppercase tracking-wide font-bold">{title}</span>
        </div>
    );
}

function Row({ icon: Icon, label, value, mono, className = "" }) {
    return (
        <div className={className}>
            <dt className="text-[10px] uppercase tracking-wide font-bold text-muted-foreground mb-0.5 flex items-center gap-1">
                {Icon && <Icon size={11} />} {label}
            </dt>
            <dd className={mono ? "font-mono text-sm break-all" : "text-sm break-words"}>
                {value === null || value === undefined || value === "" ? <span className="text-muted-foreground">—</span> : value}
            </dd>
        </div>
    );
}
